/**
 * Fragment de routes du paquet v3-p08-ecrans-technologies-agents (vague V3), rendu par le registre v10. Composants
 * chargés à la demande (règle de chargement du registre). Le projet est passé par `?projet=`, sinon celui de la coquille.
 * Routes du socle remplacées (retrait de l'historique, D2 ; YAML `couvre: route:…`) :
 *  - `/technologies` → projet-technologies (ChoixEtBaseline) ;
 *  - `/technologies/changements` → projet-changements (DemandesChangementProjet) ;
 *  - `/technologies/changements/:changeId` → projet-changement-fiche (DemandeChangementProjet) ;
 *  - `/workflow` → projet-agents (WorkflowConsole).
 * Routes neuves : les deux autres onglets, le lancement et les sept dialogues (chacun sa route).
 */
import { lazy, Suspense, type ComponentType, type JSX } from 'react'

import type { Session } from '../../auth/session'
import type { ContexteRoutes, FragmentRoutes } from './registre'

const ProjetTechnologies = lazy(() => import('../ecrans/v3/technologies-agents/ProjetTechnologies'))
const ProjetTechnologiesCategories = lazy(() => import('../ecrans/v3/technologies-agents/ProjetTechnologiesCategories'))
const ProjetTechnologiesHistorique = lazy(() => import('../ecrans/v3/technologies-agents/ProjetTechnologiesHistorique'))
const ProjetLancement = lazy(() => import('../ecrans/v3/technologies-agents/ProjetLancement'))
const DialogueDemanderDerogationTechnologiqueProjet = lazy(() => import('../ecrans/v3/technologies-agents/DialogueDemanderDerogationTechnologiqueProjet'))
const ProjetChangements = lazy(() => import('../ecrans/v3/technologies-agents/ProjetChangements'))
const DialogueNouveauChangementProjet = lazy(() => import('../ecrans/v3/technologies-agents/DialogueNouveauChangementProjet'))
const ProjetChangementFiche = lazy(() => import('../ecrans/v3/technologies-agents/ProjetChangementFiche'))
const DialogueDeciderChangementProjet = lazy(() => import('../ecrans/v3/technologies-agents/DialogueDeciderChangementProjet'))
const DialogueDemanderDerogationEcart = lazy(() => import('../ecrans/v3/technologies-agents/DialogueDemanderDerogationEcart'))
const ProjetAgents = lazy(() => import('../ecrans/v3/technologies-agents/ProjetAgents'))
const DialogueCreerAgentProjet = lazy(() => import('../ecrans/v3/technologies-agents/DialogueCreerAgentProjet'))
const ProjetAgentFiche = lazy(() => import('../ecrans/v3/technologies-agents/ProjetAgentFiche'))
const DialogueModifierAgentProjet = lazy(() => import('../ecrans/v3/technologies-agents/DialogueModifierAgentProjet'))
const DialogueDeclarerFinalite = lazy(() => import('../ecrans/v3/technologies-agents/DialogueDeclarerFinalite'))

function ecran(Composant: ComponentType<{ session: Session }>) {
  return ({ session, t }: ContexteRoutes): JSX.Element => (
    <Suspense fallback={<p role="status">{t('v10.primitives.assemblage.chargement')}</p>}>
      <Composant session={session} />
    </Suspense>
  )
}

export const fragment: FragmentRoutes = {
  zone: 'v3-technologies-agents',
  routes: [
    { chemin: '/technologies', element: ecran(ProjetTechnologies), remplace: true },
    { chemin: '/technologies/categories', element: ecran(ProjetTechnologiesCategories) },
    { chemin: '/technologies/historique', element: ecran(ProjetTechnologiesHistorique) },
    { chemin: '/technologies/lancement', element: ecran(ProjetLancement) },
    { chemin: '/technologies/derogation', element: ecran(DialogueDemanderDerogationTechnologiqueProjet) },
    { chemin: '/technologies/changements', element: ecran(ProjetChangements), remplace: true },
    { chemin: '/technologies/changements/nouveau', element: ecran(DialogueNouveauChangementProjet) },
    { chemin: '/technologies/changements/:changeId', element: ecran(ProjetChangementFiche), remplace: true },
    { chemin: '/technologies/changements/:changeId/decider', element: ecran(DialogueDeciderChangementProjet) },
    { chemin: '/technologies/changements/:changeId/derogation', element: ecran(DialogueDemanderDerogationEcart) },
    { chemin: '/workflow', element: ecran(ProjetAgents), remplace: true },
    { chemin: '/workflow/agents/nouveau', element: ecran(DialogueCreerAgentProjet) },
    { chemin: '/workflow/agents/:agentId', element: ecran(ProjetAgentFiche) },
    { chemin: '/workflow/agents/:agentId/modifier', element: ecran(DialogueModifierAgentProjet) },
    { chemin: '/workflow/finalites/declarer', element: ecran(DialogueDeclarerFinalite) },
  ],
}
